import { Component, ViewEncapsulation, OnInit, OnDestroy, HostListener, NgZone } from '@angular/core';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AppInsightsServiceDirective } from './shared/services/app-insights.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { HeaderFooterVisibilityService } from '@app/shared/services/header-footer-visibility.service';
import { environment } from '@env/environment';
import 'globalize/number';
import 'globalize/date';
import 'globalize/plural';
import { LocalDatePipe } from './shared/pipes/local-date.pipe';
import moment from 'moment';
import { MAT_DATE_LOCALE, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { ExtendedDateFormat, GERMAN_DATE_FORMAT } from '@app/shared/constants';
import { NotificationDialogComponent } from './shared/dialog/notification-dialog/notification-dialog.component';
import { FeatureActivityLoggerService } from './shared/services/loggers/feature-activity-logger.service';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { StringUtility } from './shared/string-utility';
import { HEADER_CONTAINER } from './shared/constants';
import { AuthService } from './shared/services/auth.service';

const Globalize = require('globalize');

Globalize.load(
  require('cldr-data/supplemental/numberingSystems.json'),
  require('cldr-data/supplemental/likelySubtags.json'),
  require('cldr-data/main/de/numbers.json'),
  require('cldr-data/main/en/numbers.json'),
  require('cldr-data/main/de/ca-gregorian.json'),
  require('cldr-data/main/de/timeZoneNames.json'),
  require('cldr-data/main/en/ca-gregorian.json'),
  require('cldr-data/main/en/timeZoneNames.json'),
  require('cldr-data/supplemental/timeData.json'),
  require('cldr-data/supplemental/weekData.json'),
);

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: environment.language },
    { provide: DateAdapter, useClass: MomentDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: GERMAN_DATE_FORMAT },
    LocalDatePipe,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, OnDestroy {
  public routerSubscription?: Subscription;
  public isMap = false;
  public isDashboard = false;
  public isRegistrationRequest = false;
  public isEPContainer = false;
  public isLoading = false;
  environment?: string;
  indicator?: string;

  constructor(
    public router: Router,
    public overlayLoaderService: OverlayLoaderService,
    public appInsightsServiceDirective: AppInsightsServiceDirective,
    public headerFooterVisibilityService: HeaderFooterVisibilityService,
    public translate: TranslateService,
    private readonly matDialog: MatDialog,
    private readonly featureActivityLogger: FeatureActivityLoggerService,
    private readonly ngZone: NgZone,
    private readonly authService: AuthService,
  ) {}

  ngOnInit() {
    this.environment = environment.environment.toLowerCase();
    this.indicator = environment.indicatorText;
    this.translate.use('translations');
    Globalize.locale(environment.language);
    moment.locale(environment.language);
    registerLocaleData(localeDe, environment.language);
    this.showMaintenanceBanner();
    this.featureActivityLogger.initialize();
    this.routerSubscription = this.router.events.subscribe((event) => {
      this.navigationInterceptor(event);
    });
    this.overlayLoaderService.changes.subscribe((value) => (this.isLoading = value));

    if (this.environment === 'development') {
      this.indicator = 'DEV - Entwicklungsumgebung';
    } else if (this.environment === 'staging') {
      this.indicator = 'QA - Testumgebung';
    }

    setTimeout(() => {
      this.authService.restorePostLoginRedirectIfNeeded();
    }, 0);
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  public navigationInterceptor(event: any) {
    if (event instanceof NavigationStart && this.overlayLoaderService.isGlobalLoader) {
      this.overlayLoaderService.isLoading = true;
    }

    if (event instanceof NavigationEnd) {
      if (this.overlayLoaderService.isGlobalLoader) {
        this.overlayLoaderService.isLoading = false;
      }

      this.isMap = this.router.url.indexOf('/energiekarte') >= 0 || this.router.url.indexOf('/vertriebstool') >= 0;
      this.isRegistrationRequest = this.router.url.indexOf('/registrationrequest') >= 0;
      this.isDashboard = StringUtility.hasAnyMatchInArray(this.router.url.split('/').filter(Boolean), HEADER_CONTAINER.Responsive);
      this.headerFooterVisibilityService.isFooterVisible = !this.isMap;
      this.isEPContainer = StringUtility.hasAnyMatchInArray(this.router.url.split('/').filter(Boolean), HEADER_CONTAINER.EP);

      this.headerFooterVisibilityService.isHeaderVisible = !(this.router.url.indexOf('/registrieren') >= 0 || this.router.url.indexOf('/error') >= 0);
    }
  }

  showMaintenanceBanner() {
    if (
      environment.maintenanceEndDate &&
      environment.maintenanceMessage &&
      environment.maintenanceMessage.trim().length > 0 &&
      moment() <= moment(environment.maintenanceEndDate, ExtendedDateFormat) &&
      sessionStorage.getItem('maintenance') === '1'
    ) {
      this.matDialog
        .open(NotificationDialogComponent, {
          data: {
            descriptionText: environment.maintenanceMessage,
            descriptionFaClass: 'fa-exclamation-triangle fa-2x',
            orientation: 'row',
            alignment: 'start',
          },
          width: '700px',
          position: { top: '20%' },
        })
        .afterOpened()
        .subscribe(() => sessionStorage.setItem('maintenance', '0'));
    }
  }

  @HostListener('window:mfeNavigate', ['$event'])
  onMfeNavigate(event: CustomEvent) {
    this.ngZone.run(() => this.router.navigateByUrl(event.detail));
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(evt: KeyboardEvent) {
    if (evt.key === 'Backspace') {
      let doPrevent = true;
      const types = [
        'text',
        'password',
        'file',
        'search',
        'email',
        'number',
        'date',
        'color',
        'datetime',
        'datetime-local',
        'month',
        'range',
        'search',
        'tel',
        'time',
        'url',
        'week',
      ];
      const target = <HTMLInputElement>evt.target;

      const disabled = target.disabled || (<HTMLInputElement>evt.target).readOnly;
      if (!disabled) {
        if (target.isContentEditable) {
          doPrevent = false;
        } else if (target.nodeName === 'INPUT') {
          let type = target.type;
          if (type) {
            type = type.toLowerCase();
          }
          if (types.indexOf(type) > -1) {
            doPrevent = false;
          }
        } else if (target.nodeName === 'TEXTAREA') {
          doPrevent = false;
        }
      }
      if (doPrevent) {
        evt.preventDefault();
        return false;
      }
    }
  }
}
